// ───────────────────────────────────────────────────────────────────
// Period-pill row — FUNCTIONAL card-level window selector (SP-1b; §7 Q2). The
// five pinned pills render in FIXED order (WINDOW_KEYS). Selecting a window
// re-reads every WINDOWED figure's matching `windows[]` entry
// (value/count/coverage) — purely client-side over the delivered payload, NO
// refetch. An `absent` window shows "not available" (never a scaled number).
// Snapshot figures ignore it. The parent HIDES this row entirely when a card has
// NO windowed figures.
//
// ME-CARDS-R1 A2: when the card's TURN window is not one of the five pinned keys
// (the live arm card resolved to "yesterday"), a LEADING sixth pill represents
// that turn window and is selected initially — otherwise the turn-resolved read
// the verdict was computed on is unreachable from the pill row and the card
// renders "not available" over real data.
// ───────────────────────────────────────────────────────────────────
import React from "react";

import type {
  TurnWindow,
  WindowSelection,
} from "../../../../../../shared/components/machineEvidence";
import { WINDOW_KEYS } from "../../../../../../shared/components/machineEvidence";
import useTranslation from "../../../../../../shared/hooks/useTranslation";
import { dt } from "../../../../../../shared/themes/redesignTokens";
import { WINDOW_PILL_LABELS } from "../machineCardConstants";
import { needsTurnPill, turnWindowLabel } from "./sectionChrome";

const pillStyle = (active: boolean): React.CSSProperties => ({
  padding: "2px 10px",
  borderRadius: dt.radiusPill,
  fontFamily: dt.fontMono,
  fontSize: 11,
  fontWeight: active ? 600 : 500,
  color: active ? dt.textPrimary : dt.textSecondary,
  background: active ? dt.blueSoft : dt.inputBg,
  border: `1px solid ${active ? dt.cardBorder : "transparent"}`,
  cursor: "pointer",
});

export const PeriodPillRow: React.FC<{
  selection: WindowSelection;
  /** the card's turn-resolved primary window (ME-CARDS-R1 A2). */
  turnWindow: TurnWindow;
  onSelect: (selection: WindowSelection) => void;
}> = ({ selection, turnWindow, onSelect }) => {
  const { translate } = useTranslation("machineEvidence");
  const showTurn = needsTurnPill(turnWindow);
  const turn = showTurn ? turnWindowLabel(turnWindow, translate) : null;

  return (
    <div
      className="msc-period-pills"
      role="tablist"
      aria-label="Period window"
      style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}
    >
      {turn ? (
        <button
          type="button"
          role="tab"
          aria-selected={selection.kind === "turn"}
          data-window-key="__turn__"
          data-turn-token={turnWindow.token ?? ""}
          data-turn-verbatim={turn.verbatim ? "true" : "false"}
          onClick={() => onSelect({ kind: "turn" })}
          style={pillStyle(selection.kind === "turn")}
        >
          {turn.label}
        </button>
      ) : null}
      {WINDOW_KEYS.map((key) => {
        const active = selection.kind === "window" && selection.key === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            data-window-key={key}
            onClick={() => onSelect({ kind: "window", key })}
            style={pillStyle(active)}
          >
            {translate(`window.${key}`, { defaultValue: WINDOW_PILL_LABELS[key] }) as string}
          </button>
        );
      })}
    </div>
  );
};

export default PeriodPillRow;
